<template>
    <div class="border bg-white rounded-lg shadow p-4 relative">
        <h2 class="mb-4">
            {{ label }}
            <span v-if="form.id">
                <a :href="showRoute" target="_blank">
                    (ID: {{ form.id }})
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 inline-block"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                    </svg>
                </a>
            </span>

            <span v-if="resettable" v-tooltip="{ content: 'Reset Link', container: false, placement: 'top', html: false, }" class="cursor-pointer" @click="resetLink">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="ml-2 w-5 h-5 inline-block">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
            </span>
        </h2>

        <input
            type="hidden"
            name="id"
            :value="form.id"
        />

        <Select
            placeholder="Link Type"
            name="link_type_id"
            v-model="form.link_type_id"
            class="mt-2"
            @input="linkTypeChanged"
            :disabled="disabled"
            :options="enabledLinkTypes"
            :error="errors?.link_type_id ? errors?.link_type_id[0] : null"
        />

        <input-text
            v-if="! hideLinkText"
            placeholder="Link Text"
            name="link_text"
            v-model="form.link_text"
            :disabled="disabled"
            :error="errors?.link_text ? errors?.link_text[0] : null"
            class="mt-3"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            v-if="Number(form.link_type_id) === 2"
        >
            <polymorphic-reference
                link-text="Link an existing document"
                :creatable="false"
                :language="language"
                v-model="form.document"
                :disabled="disabled"
                :models="linkableTypes"
                :should-open-new-window=true
                :query="{
                    status_id: 1,
                }"
            />
        </div>

        <div class="grid grid-cols-4" v-if="Number(form.link_type_id) === 3">
            <div class="mt-3">
                <Select
                    placeholder="Protocol"
                    name="key"
                    class="col-span-1"
                    :disabled="disabled"
                    v-model="form.protocol"
                    :options="protocolOptions"
                />
            </div>
            <input-text
                name="url_part"
                v-model="form.url_part"
                :error="errors?.url_part ? errors?.url_part[0] : null"
                :disabled="disabled"
                class="mt-3 col-span-3"
                :placeholder="urlPlaceholder"
                @input="checkUrl"
            />

            <input-text
                :disabled="true"
                :value="fullUrl"
                class="mt-3 col-span-4"
            />
        </div>

        <input-text
            v-if="[1, 2].includes(Number(form.link_type_id))"
            name="anchor"
            placeholder="Jump ID"
            v-model="form.anchor"
            :disabled="disabled"
            :error="errors?.anchor ? errors?.anchor[0] : null"
            class="mt-3"
        >
            <template v-slot:prepend>
                <span class="block mt-2">#</span>
            </template>
        </input-text>

        <input-text
            v-if="Number(form.link_type_id) === 3"
            placeholder="Rel Override"
            name="rel_override"
            v-model="form.rel_override"
            :disabled="disabled"
            :error="errors?.rel_override ? errors?.rel_override[0] : null"
            class="mt-3"
        />

        <div
            v-if="Number(form.link_type_id) === 4"
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <reference
                link-text="Link an existing affiliate link"
                v-model="form.affiliate_link"
                model-data="Affiliate Link"
                :language="language"
                :disabled="disabled"
                :creatable="false"
                :editable="false"
                :linkable="true"
                :error="errors?.affiliate_link_id ? errors?.affiliate_link_id[0] : null"
            />
        </div>

        <input-checkbox
            v-if="[2, 3, 4].includes(Number(form.link_type_id))"
            name="new_tab"
            label="Opens in new tab?"
            class="mt-3"
            :disabled="disabled"
            v-model="form.new_tab"
            :error="errors?.new_tab ? errors?.new_tab[0] : null"
        />

    </div>
</template>

<script>
import InputText from "../form/InputText";
import reference from "../../mixins/reference";
import {v4 as uuidv4} from "uuid";

export default {
    name: "RocketLink",
    components: {InputText},
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        resettable: {
            type: Boolean,
            required: false,
            default: false,
        },
        value: {
            type: Object,
            required: false,
        },
        label: {
            type: String,
            required: false,
            default: 'Link',
        },
        language: {
            type: Object,
            required: true,
        },
        errors: {
            type: [Array, Object],
            required: false,
        },
        linkTypes: {
            type: Array, // Array of link type ids: [1, 3, 4]
            required: false,
            default: () => [1, 2, 3, 4],
        },
        linkableTypes: {
            type: Array, // Array of data types
            required: false,
            default: () => [
                'All Category',
                'Casino Review',
                'Slot Review',
                'Game Review',
                'Home Page',
                'About Us Page',
                'Author',
                'Legal Notice Page',
                'Privacy Policy Page',
                'Cookie Policy Page',
                'Responsible Gambling Page',
                'Payment Method Main Page',
                'License Main Page',
                'Knowledge Hub Page',
                'Blog Main Page',
                'Guide Main Page',
                'Trust Page',
                'Blog',
                'Guide',
                'Html Sitemap',
            ],
            validator: (types) => {
                return types.filter(type => ! Object.hasOwn(reference.data().contentTypes, type)).length === 0;
            },
        },
        hideLinkText: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    mounted: function () {
        this.setForm();
    },
    methods: {
        checkUrl() {
            let newForm = {...this.form};

            // if url_part contains protocol,
            // auto select protocol and remove protocol from url_part
            this.protocolOptions.forEach(options => {
                if (newForm.url_part.startsWith(options.value)) {
                    newForm.protocol = options.value;
                    newForm.url_part = newForm.url_part.replace(options.value, '');
                }
            });

            if (['https://', 'http://'].includes(newForm.protocol)) {
                // check for invalid URL
                try {
                    new URL(this.fullUrl);
                }
                catch (e) {
                    this.$notify({
                        group: "formError",
                        title: "Invalid URL",
                        text: `The url "${this.fullUrl}" is invalid`,
                    });
                }
            }

            this.form = newForm;
        },
        linkTypeChanged() {
            let newForm = {...this.form};

            switch (Number(newForm.link_type_id)) {
                case 1:
                    // Jump Link
                    newForm.document = null;
                    newForm.protocol = null;
                    newForm.url_part = null;
                    newForm.rel_override = null;
                    newForm.affiliate_link = null;
                    newForm.new_tab = false;
                    break;
                case 2:
                    // Internal Link
                    newForm.protocol = null;
                    newForm.url_part = null;
                    newForm.rel_override = null;
                    newForm.affiliate_link = null;
                    newForm.new_tab = false;
                    break;
                case 3:
                    // External Link
                    newForm.anchor = null;
                    newForm.document = null;
                    newForm.affiliate_link = null;
                    newForm.new_tab = true;
                    break;
                case 4:
                    // Pretty Link
                    newForm.anchor = null;
                    newForm.document = null;
                    newForm.protocol = null;
                    newForm.url_part = null;
                    newForm.rel_override = null;
                    newForm.new_tab = true;
                    break;
            }

            // not sure why emitting input works better than `this.form = newForm;`
            // setting `this.form` directly keeps resetting new_tab to false for external links
            this.$emit('input', newForm);
        },
        resetLink() {
            this.$confirm({
                message: `Reset Link?`,
                button: {
                    no: "No",
                    yes: "Yes",
                },
                callback: (confirm) => {
                    if (confirm) {
                        let newForm = {...this.form};

                        newForm.id = null;
                        newForm.link_type_id = null;
                        newForm.link_text = null;
                        newForm.document = null;
                        newForm.document_id = null;
                        newForm.document_type = null;
                        newForm.protocol = null;
                        newForm.url_part = null;
                        newForm.anchor = null;
                        newForm.rel_override = null;
                        newForm.affiliate_link = null;
                        newForm.affiliate_link_id = null;
                        newForm.new_tab = false;
                        newForm.language_id = null;

                        // not sure why emitting input works better than `this.form = newForm;`
                        // setting `this.form` directly keeps resetting new_tab to false for external links
                        this.$emit('input', newForm);
                    }
                },
            });
        },
        setForm() {
            if (! this.form.language_id) {
                this.form.language_id = this.language.id;
            }
            if (this.value) {
                this.form.id = this.value.id || null;
                this.form.link_type_id = this.value.link_type_id || null;
                this.form.link_text = this.value.link_text || null;
                this.form.document = this.value.document || null;
                this.form.document_id = this.value.document_id || null;
                this.form.document_type = this.value.document_type || null;

                if (this.value.url) {
                    this.protocolOptions.forEach(options => {
                        if (this.value.url.startsWith(options.value)) {
                            this.form.protocol = options.value;
                            this.form.url_part = this.value.url.replace(options.value, '');
                        }
                    });
                }
                else {
                    this.form.protocol = this.value.protocol || null;
                    this.form.url_part = this.value.url_part || null;
                }

                this.form.anchor = this.value.anchor || null;
                this.form.rel_override = this.value.rel_override || null;
                this.form.affiliate_link = this.value.affiliate_link || null;
                this.form.affiliate_link_id = this.value.affiliate_link_id || null;
                this.form.new_tab = this.value.new_tab || false;

                this.form.uuid = this.value.uuid || uuidv4();
            }
        },
    },
    computed: {
        enabledLinkTypes() {
            let availableLinkTypes = [
                { value: 1, name: 'Jump Link' },
                { value: 2, name: 'Internal Link' },
                { value: 3, name: 'External Link' },
                { value: 4, name: 'Pretty Link' },
            ];

            return availableLinkTypes.filter(item => this.linkTypes.includes(item.value));
        },
        fullUrl() {
            return `${this.form.protocol || ''}${this.form.url_part || ''}`;
        },
        showRoute() {
            return route('links.show', [this.form.id]);
        },
        urlPlaceholder() {
            switch(this.form.protocol) {
                case 'https://': return 'Url';
                case 'http://': return 'Url';
                case 'mailto:': return 'Email';
                case 'tel:': return 'Phone';
            }
            return 'Url';
        },
    },
    data(){
        return {
            form: {
                id: null,
                link_type_id: null,
                link_text: null,
                document: null,
                document_id: null,
                document_type: null,
                protocol: null,
                url_part: null,
                anchor: null,
                rel_override: null,
                affiliate_link: null,
                affiliate_link_id: null,
                new_tab: false,
                language_id: null,

                uuid: null,
            },
            protocolOptions: [
                {value: 'https://', name: 'https://'},
                {value: 'http://', name: 'http://'},
                {value: 'mailto:', name: 'mailto:'},
                {value: 'tel:', name: 'tel:'},
            ],
        }
    },
    watch: {
        value: {
            deep: true,
            handler: function (newValue) {
                this.setForm();
            },
        },
        form: {
            deep: true,
            handler: function (newValue) {
                this.$emit('input', newValue);
            },
        },
        "form.affiliate_link": function (newValue) {
            this.form.affiliate_link_id = newValue ? newValue.id : null;
        },
        "form.document": function (newValue) {
            this.form.document_id = newValue?.id || null;
            this.form.document_type = newValue?.model_name || null;
        },
    },
}
</script>
